// frontend/src/pages/admin/Documents.js
//
// Invoices and credit notes. The generator has been running since the launch
// round; until now the only way to see a document was to guess its id and type
// the URL, which meant nobody was checking what was being issued.
import React, { useCallback, useEffect, useRef, useState } from 'react';
import AdminSidebar from '../../components/AdminSidebar';
import api from '../../utils/api';
import toast from 'react-hot-toast';
import { useTranslation } from 'react-i18next';
import { format } from 'date-fns';
import { el as elLocale } from 'date-fns/locale';
import { AlertTriangle, RefreshCw, FileText, Download, ExternalLink } from 'lucide-react';
import theme from '../../theme';

// The self-hosted copy in public/fonts, referenced once from index.html.
// This used to be a Google Fonts URL injected per page, which disclosed every
// visitor's IP to a third party on every page load and contradicted the privacy
// policy's "no third-party requests" claim. Kept as a constant because several
// pages import it into an @import rule rather than a <link>.
const FONTS = '/fonts/fonts.css';

const PAGE_SIZE = 50;

// The interceptor flattens a JSON error body and adds `status`; `err.error` is
// the machine code and `err.message` the sentence worth showing. Anything
// without `error` is a raw axios failure whose own `.message` is
// "Request failed with status code 500" — never something to show an operator.
const serverText = (err, fallback) =>
  (err && typeof err === 'object' && err.error) ? (err.message || err.error) : fallback;

// yyyy-MM-dd, which is both what <input type="date"> speaks and what the API
// expects — going through toISOString() instead would shift a Cyprus evening
// into the previous UTC day and quietly drop a day of takings off the totals.
const isoDay = (d) => format(d, 'yyyy-MM-dd');

const startOfThisMonth = () => {
  const now = new Date();
  return new Date(now.getFullYear(), now.getMonth(), 1);
};

// The document is HTML behind a bearer token, so it can never be a plain <a
// href>: the browser sends no Authorization header on a top-level navigation and
// would land on a 401 page. The tab therefore has to be opened synchronously
// inside the click — a window.open issued after an await is treated as an
// unsolicited popup and blocked — and filled in once the markup arrives.
//
// When there is no window, the old fallback appended an <a download> on a blob:
// URL and toasted "saved to your device". In the Capacitor WebView that does
// nothing whatsoever — no DownloadListener is registered — so the success was
// invented. The document is shown in-app instead.
const writeIntoWindow = (win, html) => {
  if (win && !win.closed) {
    win.document.open();
    win.document.write(html);
    win.document.close();
    return true;
  }
  return false;
};

// A Capacitor WebView cannot save a file: <a download> is ignored outright, and
// there is no DownloadListener behind it. Anything whose only delivery is a
// download therefore has to say so rather than appear to work.
const isNativeApp = () => {
  const cap = typeof window !== 'undefined' ? window.Capacitor : null;
  if (!cap) return false;
  return typeof cap.isNativePlatform === 'function' ? cap.isNativePlatform() : !!cap.isNative;
};

export default function AdminDocuments() {
  const { t, i18n } = useTranslation();
  const dateLocale = i18n.language === 'el' ? elLocale : undefined;

  const [from, setFrom] = useState(isoDay(startOfThisMonth()));
  const [to, setTo]     = useState(isoDay(new Date()));
  const [docType, setDocType] = useState('all');
  const [page, setPage] = useState(1);

  const [documents, setDocuments] = useState([]);
  const [totals, setTotals]       = useState(null);
  const [loading, setLoading]     = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [openingId, setOpeningId] = useState(null);
  // The document itself, when no window could be opened for it.
  const [docView, setDocView] = useState(null); // { html, title }
  // Two loads can be in flight after a quick filter change, and they do not
  // answer in the order they were sent — an older answer landing last put a
  // different month's invoices under the current filter.
  const loadSeq = useRef(0);
  const revenueSeq = useRef(0);

  // Configuration status. null while unknown — the warning must not flash on
  // screen before we know whether it applies, nor be assumed away if the check
  // itself fails.
  const [config, setConfig] = useState(null);

  // Revenue is being added in a parallel change. A 404 means "not deployed yet",
  // which is a reason to hide the section, not to redden the whole page.
  const [revenue, setRevenue]           = useState(null);
  const [revenueLive, setRevenueLive]   = useState(true);
  const [revenueLoading, setRevenueLoading] = useState(false);
  const [exporting, setExporting]       = useState(false);

  useEffect(() => {
    const link = document.createElement('link'); link.rel = 'stylesheet'; link.href = FONTS; document.head.appendChild(link);
    return () => document.head.removeChild(link);
  }, []);

  useEffect(() => {
    api.get('/invoices/status')
      .then(setConfig)
      // If the check cannot be reached we say nothing rather than promising the
      // documents are valid — the banner below treats "unknown" as "unknown".
      .catch(() => setConfig(null));
  }, []);

  useEffect(() => { setPage(1); }, [from, to, docType]);

  const rangeParams = useCallback(() => {
    const p = new URLSearchParams();
    if (from) p.set('from', from);
    if (to) p.set('to', to);
    return p;
  }, [from, to]);

  const load = useCallback(() => {
    setLoading(true);
    setLoadError(null);
    const seq = ++loadSeq.current;
    const params = rangeParams();
    params.set('limit', PAGE_SIZE);
    params.set('offset', (page - 1) * PAGE_SIZE);
    if (docType !== 'all') params.set('doc_type', docType);
    api.get(`/invoices?${params}`)
      .then(d => {
        if (seq !== loadSeq.current) return; // a newer filter is already loading
        setDocuments(Array.isArray(d?.documents) ? d.documents : []);
        setTotals(d?.totals || null);
      })
      .catch(err => {
        if (seq !== loadSeq.current) return;
        // An empty table where an accountant expects a month of invoices is a
        // statement about the business, not about the network. Say which it is.
        setDocuments([]);
        setTotals(null);
        setLoadError(serverText(err, t('admin.documents.loadFailedDetail')));
      })
      .finally(() => { if (seq === loadSeq.current) setLoading(false); });
  }, [rangeParams, page, docType, t]);

  useEffect(() => { load(); }, [load]);

  const loadRevenue = useCallback(() => {
    if (!revenueLive) return;
    setRevenueLoading(true);
    const seq = ++revenueSeq.current;
    const params = rangeParams();
    params.set('group', 'day');
    api.get(`/reports/revenue?${params}`)
      .then(d => { if (seq === revenueSeq.current) setRevenue(d); })
      .catch(err => {
        if (seq !== revenueSeq.current) return;
        // Both shapes are checked on purpose: the interceptor only attaches
        // `status` when the server sent a JSON body, and a route that does not
        // exist yet answers with the bare 404 page, which arrives as the raw
        // axios error instead.
        if (err?.status === 404 || err?.response?.status === 404) {
          // Endpoint not deployed. Hide the section for the rest of the session
          // instead of retrying it on every filter change.
          setRevenueLive(false);
          setRevenue(null);
          return;
        }
        setRevenue(null);
      })
      .finally(() => { if (seq === revenueSeq.current) setRevenueLoading(false); });
  }, [rangeParams, revenueLive]);

  useEffect(() => { loadRevenue(); }, [loadRevenue]);

  const openDocument = async (doc) => {
    // Opened before the await, for the popup-blocker reason above.
    const win = window.open('', '_blank');
    setOpeningId(doc.id);
    try {
      const html = await api.get(`/invoices/${doc.id}`, { responseType: 'text' });
      const markup = typeof html === 'string' ? html : String(html);
      if (!writeIntoWindow(win, markup)) setDocView({ html: markup, title: doc.document_number });
    } catch (err) {
      if (win && !win.closed) win.close();
      toast.error(serverText(err, t('admin.documents.openFailed')), { duration: 8000 });
    } finally {
      setOpeningId(null);
    }
  };

  const exportCsv = async () => {
    // Nothing here can write a file inside the app's WebView, and pretending
    // otherwise leaves an operator hunting a download that never happened.
    if (isNativeApp()) {
      toast.error(t('admin.documents.exportNeedsBrowser'), { duration: 8000 });
      return;
    }
    setExporting(true);
    try {
      // Same bearer-token problem as the documents: a link would arrive
      // unauthenticated, so the file is fetched and handed to the browser.
      const blob = await api.get(`/reports/revenue.csv?${rangeParams()}`, { responseType: 'blob' });
      const url = URL.createObjectURL(blob instanceof Blob ? blob : new Blob([blob], { type: 'text/csv' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = `glamgo-revenue-${from}-${to}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (err) {
      if (err?.status === 404 || err?.response?.status === 404) setRevenueLive(false);
      toast.error(serverText(err, t('admin.documents.exportFailed')), { duration: 8000 });
    } finally {
      setExporting(false);
    }
  };

  const money = (v) => `€${(parseFloat(v) || 0).toFixed(2)}`;
  const totalPages = totals ? Math.max(1, Math.ceil((Number(totals.count) || 0) / PAGE_SIZE)) : 1;
  const specimen = config && config.configured === false;

  return (
    <div style={{ minHeight: '100vh', background: theme.grey100, fontFamily: theme.font.body, display: 'flex' }}>
      <AdminSidebar />

      <div className="admin-main" style={{ padding: '40px' }}>
        <h1 style={{ fontFamily: theme.font.display, fontSize: 40, color: theme.ink, marginBottom: 4 }}>{t('admin.documents.title')}</h1>
        <p style={{ color: theme.grey400, marginBottom: 28 }}>{t('admin.documents.subtitle')}</p>

        {/* ── Specimen warning ─────────────────────────────────────────────
            The legal entity does not exist yet, so every document the system
            issues carries a "NOT A VALID TAX DOCUMENT" watermark and is never
            emailed. An operator finding that out from a customer, or an
            accountant filing one of these, is the failure this banner exists to
            prevent — so it is persistent, cannot be dismissed, and names the
            exact settings that are missing. */}
        {specimen && (
          <div style={{ background: theme.redWashWarm, border: `2px solid ${theme.redBrick}`, borderRadius: 8, padding: '22px 26px', marginBottom: 28 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 10 }}>
              <AlertTriangle size={20} color={theme.redBrick} />
              <span style={{ fontFamily: theme.font.display, fontSize: 24, color: theme.redDark }}>{t('admin.documents.specimenTitle')}</span>
            </div>
            <p style={{ color: theme.redDark, fontSize: 14, lineHeight: 1.8, margin: '0 0 12px' }}>{t('admin.documents.specimenBody')}</p>
            {Array.isArray(config.missing) && config.missing.length > 0 && (
              <>
                <div style={{ fontSize: 11, color: theme.redDark, letterSpacing: '0.1em', marginBottom: 8 }}>{t('admin.documents.specimenMissing')}</div>
                <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                  {config.missing.map(m => (
                    <code key={m} style={{ background: theme.white, border: `1px solid ${theme.redBorder}`, borderRadius: 4, padding: '4px 10px', fontSize: 12, color: theme.redDark }}>{m}</code>
                  ))}
                </div>
              </>
            )}
            {config.message && (
              <p style={{ color: theme.redDark, fontSize: 13, lineHeight: 1.7, margin: '14px 0 0' }}>{config.message}</p>
            )}
          </div>
        )}

        {/* ── Filters ──────────────────────────────────────────────────── */}
        <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', alignItems: 'flex-end', marginBottom: 24 }}>
          <div>
            <label style={labelStyle}>{t('admin.documents.fromLabel')}</label>
            <input type="date" value={from} max={to || undefined} onChange={e => setFrom(e.target.value)} style={inputStyle} />
          </div>
          <div>
            <label style={labelStyle}>{t('admin.documents.toLabel')}</label>
            <input type="date" value={to} min={from || undefined} onChange={e => setTo(e.target.value)} style={inputStyle} />
          </div>
          <div>
            <label style={labelStyle}>{t('admin.documents.typeLabel')}</label>
            <select value={docType} onChange={e => setDocType(e.target.value)} style={{ ...inputStyle, cursor: 'pointer' }}>
              <option value="all">{t('admin.documents.typeAll')}</option>
              <option value="invoice">{t('admin.documents.typeInvoice')}</option>
              <option value="credit_note">{t('admin.documents.typeCreditNote')}</option>
            </select>
          </div>
          <button onClick={load} style={{ display: 'inline-flex', alignItems: 'center', gap: 7, padding: '11px 20px', border: `1px solid ${theme.sand}`, background: theme.white, borderRadius: 4, cursor: 'pointer', fontSize: 12, color: theme.grey600, fontFamily: theme.font.body }}>
            <RefreshCw size={13} /> {t('admin.documents.refresh')}
          </button>
        </div>

        {/* ── Totals ──────────────────────────────────────────────────────
            The three figures an accountant is actually shown, kept above the
            list because the list is the supporting detail, not the answer. */}
        <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', marginBottom: 28 }}>
          {[
            { key: 'totalCount', value: totals ? String(Number(totals.count) || 0) : '—', color: theme.ink },
            { key: 'totalNet',   value: totals ? money(totals.net) : '—',   color: theme.ink },
            { key: 'totalVat',   value: totals ? money(totals.vat) : '—',   color: theme.amber },
            { key: 'totalGross', value: totals ? money(totals.gross) : '—', color: theme.greenDeep },
          ].map(card => (
            <div key={card.key} style={{ background: theme.white, border: `1px solid ${theme.creamBone}`, borderRadius: 10, padding: '18px 26px', minWidth: 150, flex: '0 1 auto' }}>
              <div style={{ fontFamily: theme.font.display, fontSize: 30, lineHeight: 1.1, color: card.color }}>{card.value}</div>
              <div style={{ fontSize: 11, color: theme.grey400, marginTop: 6, letterSpacing: '0.06em' }}>{t(`admin.documents.${card.key}`)}</div>
            </div>
          ))}
        </div>

        {/* ── Documents ───────────────────────────────────────────────── */}
        <div style={{ background: theme.white, border: `1px solid ${theme.creamBone}`, borderRadius: 4, overflow: 'hidden' }}>
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
              <thead>
                <tr style={{ background: theme.cream }}>
                  {['colNumber', 'colType', 'colIssued', 'colBillTo', 'colNet', 'colVat', 'colGross', ''].map((h, i) => (
                    <th key={h || i} style={{ padding: '12px 14px', textAlign: 'left', color: theme.grey400, fontWeight: 500, fontSize: 11, letterSpacing: '0.08em', whiteSpace: 'nowrap' }}>
                      {h ? t(`admin.documents.${h}`) : ''}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr><td colSpan={8} style={{ padding: 48, textAlign: 'center', color: theme.gold, fontFamily: theme.font.display, fontSize: 20 }}>{t('admin.documents.loading')}</td></tr>
                ) : loadError ? (
                  <tr><td colSpan={8} style={{ padding: 48, textAlign: 'center' }}>
                    <AlertTriangle size={24} color={theme.redBrick} />
                    <div style={{ fontFamily: theme.font.display, fontSize: 22, color: theme.ink, margin: '10px 0 6px' }}>{t('admin.documents.loadFailedTitle')}</div>
                    <p style={{ color: theme.grey400, fontSize: 13, margin: '0 0 18px' }}>{loadError}</p>
                    <button onClick={load} style={{ display: 'inline-flex', alignItems: 'center', gap: 7, background: theme.ink, color: theme.white, border: 'none', padding: '11px 24px', borderRadius: 6, cursor: 'pointer', fontSize: 12, letterSpacing: '0.1em', fontFamily: theme.font.body }}>
                      <RefreshCw size={13} /> {t('admin.documents.retry')}
                    </button>
                  </td></tr>
                ) : documents.length === 0 ? (
                  <tr><td colSpan={8} style={{ padding: 48, textAlign: 'center', color: theme.grey400 }}>{t('admin.documents.empty')}</td></tr>
                ) : documents.map(d => {
                  const credit = d.doc_type === 'credit_note';
                  return (
                    <tr key={d.id} style={{ borderBottom: `1px solid ${theme.creamDeep}` }}>
                      <td style={{ padding: '13px 14px', fontFamily: theme.font.mono, color: theme.ink, whiteSpace: 'nowrap' }}>{d.document_number}</td>
                      <td style={{ padding: '13px 14px' }}>
                        <span style={{ background: credit ? theme.redWashWarm : theme.creamDeep, color: credit ? theme.redDark : theme.goldInk, padding: '3px 10px', borderRadius: 100, fontSize: 10, letterSpacing: '0.05em', whiteSpace: 'nowrap' }}>
                          {credit ? t('admin.documents.typeCreditNote') : t('admin.documents.typeInvoice')}
                        </span>
                        {/* A document issued while the company was not VAT
                            registered shows no VAT for a reason; flag it so a
                            zero in that column is not read as an error. */}
                        {d.vat_registered === false && (
                          <div style={{ fontSize: 10, color: theme.grey400, marginTop: 3 }}>{t('admin.documents.noVatTag')}</div>
                        )}
                      </td>
                      <td style={{ padding: '13px 14px', color: theme.grey600, whiteSpace: 'nowrap' }}>
                        {d.issued_at ? format(new Date(d.issued_at), 'd MMM yyyy', { locale: dateLocale }) : '—'}
                      </td>
                      <td style={{ padding: '13px 14px' }}>
                        <div style={{ color: theme.ink }}>{d.bill_to_name || '—'}</div>
                        <div style={{ color: theme.grey400, fontSize: 11 }}>{d.bill_to_email}</div>
                      </td>
                      <td style={{ padding: '13px 14px', color: theme.grey600, whiteSpace: 'nowrap' }}>{money(d.total_net)}</td>
                      <td style={{ padding: '13px 14px', color: theme.grey600, whiteSpace: 'nowrap' }}>{money(d.total_vat)}</td>
                      <td style={{ padding: '13px 14px', fontFamily: theme.font.display, fontSize: 18, color: credit ? theme.redBrick : theme.ink, whiteSpace: 'nowrap' }}>
                        {credit ? '−' : ''}{money(d.total_gross)}
                      </td>
                      <td style={{ padding: '13px 14px', whiteSpace: 'nowrap' }}>
                        <button onClick={() => openDocument(d)} disabled={openingId === d.id}
                          style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '8px 14px', border: `1px solid ${theme.sand}`, background: theme.white, borderRadius: 4, cursor: openingId === d.id ? 'wait' : 'pointer', fontSize: 12, color: theme.blue, fontFamily: theme.font.body }}>
                          <ExternalLink size={13} /> {openingId === d.id ? t('admin.documents.opening') : t('admin.documents.open')}
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>

        {!loading && !loadError && totals && (Number(totals.count) || 0) > PAGE_SIZE && (
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 20, fontSize: 13, color: theme.grey500 }}>
            <span>
              {t('admin.documents.pagination', {
                from: (page - 1) * PAGE_SIZE + 1,
                to: Math.min(page * PAGE_SIZE, Number(totals.count) || 0),
                total: Number(totals.count) || 0,
              })}
            </span>
            <div style={{ display: 'flex', gap: 8 }}>
              <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1}
                style={{ padding: '8px 16px', border: `1px solid ${theme.sand}`, borderRadius: 4, background: theme.white, cursor: page === 1 ? 'not-allowed' : 'pointer', opacity: page === 1 ? 0.4 : 1, fontFamily: theme.font.body, fontSize: 12 }}>
                {t('admin.documents.prev')}
              </button>
              <span style={{ padding: '8px 12px', background: theme.ink, color: theme.white, borderRadius: 4, fontSize: 12 }}>{page} / {totalPages}</span>
              <button onClick={() => setPage(p => p + 1)} disabled={page * PAGE_SIZE >= (Number(totals.count) || 0)}
                style={{ padding: '8px 16px', border: `1px solid ${theme.sand}`, borderRadius: 4, background: theme.white, cursor: page * PAGE_SIZE >= (Number(totals.count) || 0) ? 'not-allowed' : 'pointer', opacity: page * PAGE_SIZE >= (Number(totals.count) || 0) ? 0.4 : 1, fontFamily: theme.font.body, fontSize: 12 }}>
                {t('admin.documents.next')}
              </button>
            </div>
          </div>
        )}

        {/* ── Revenue summary ─────────────────────────────────────────────
            Documents only exist for money that was invoiced; this is the same
            period seen from the takings side, including the deposits that are
            held rather than earned. Absent entirely when the endpoint is not
            deployed, because a half-rendered financial panel invites arithmetic
            on numbers that are not there. */}
        {revenueLive && (revenue || revenueLoading) && (
          <div style={{ marginTop: 40 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 12, marginBottom: 16 }}>
              <h2 style={{ fontFamily: theme.font.display, fontSize: 28, color: theme.ink, margin: 0 }}>{t('admin.documents.revenueTitle')}</h2>
              <button onClick={exportCsv} disabled={exporting}
                style={{ display: 'inline-flex', alignItems: 'center', gap: 7, padding: '11px 22px', background: theme.ink, color: theme.white, border: 'none', borderRadius: 4, cursor: exporting ? 'wait' : 'pointer', opacity: exporting ? 0.6 : 1, fontSize: 12, letterSpacing: '0.08em', fontFamily: theme.font.body }}>
                <Download size={14} /> {exporting ? t('admin.documents.exporting') : t('admin.documents.exportCsv')}
              </button>
            </div>

            {revenueLoading && !revenue ? (
              <div style={{ background: theme.white, border: `1px solid ${theme.creamBone}`, borderRadius: 4, padding: 40, textAlign: 'center', color: theme.gold, fontFamily: theme.font.display, fontSize: 20 }}>
                {t('admin.documents.loading')}
              </div>
            ) : (
              <div style={{ background: theme.white, border: `1px solid ${theme.creamBone}`, borderRadius: 4, overflow: 'hidden' }}>
                <div style={{ overflowX: 'auto' }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                    <thead>
                      <tr style={{ background: theme.cream }}>
                        {['colPeriod', 'colRentals', 'colRentalRevenue', 'colLateFees', 'colDepositsHeld', 'colDepositsRefunded', 'colNet', 'colVat', 'colGross'].map(h => (
                          <th key={h} style={{ padding: '12px 14px', textAlign: 'left', color: theme.grey400, fontWeight: 500, fontSize: 11, letterSpacing: '0.08em', whiteSpace: 'nowrap' }}>{t(`admin.documents.${h}`)}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {(revenue?.rows || []).length === 0 ? (
                        <tr><td colSpan={9} style={{ padding: 40, textAlign: 'center', color: theme.grey400 }}>{t('admin.documents.revenueEmpty')}</td></tr>
                      ) : revenue.rows.map(r => (
                        <tr key={r.period} style={{ borderBottom: `1px solid ${theme.creamDeep}` }}>
                          <td style={{ padding: '12px 14px', color: theme.ink, whiteSpace: 'nowrap' }}>{r.period}</td>
                          <td style={{ padding: '12px 14px', color: theme.grey600 }}>{r.rentals}</td>
                          <td style={{ padding: '12px 14px', color: theme.grey600, whiteSpace: 'nowrap' }}>{money(r.rental_revenue)}</td>
                          <td style={{ padding: '12px 14px', color: theme.amber, whiteSpace: 'nowrap' }}>{money(r.late_fees)}</td>
                          <td style={{ padding: '12px 14px', color: theme.grey600, whiteSpace: 'nowrap' }}>{money(r.deposits_held)}</td>
                          <td style={{ padding: '12px 14px', color: theme.grey600, whiteSpace: 'nowrap' }}>{money(r.deposits_refunded)}</td>
                          <td style={{ padding: '12px 14px', color: theme.grey600, whiteSpace: 'nowrap' }}>{money(r.net)}</td>
                          <td style={{ padding: '12px 14px', color: theme.grey600, whiteSpace: 'nowrap' }}>{money(r.vat)}</td>
                          <td style={{ padding: '12px 14px', color: theme.ink, whiteSpace: 'nowrap' }}>{money(r.gross)}</td>
                        </tr>
                      ))}
                    </tbody>
                    {revenue?.totals && (
                      <tfoot>
                        <tr style={{ background: theme.cream, fontWeight: 500 }}>
                          <td style={{ padding: '13px 14px', color: theme.ink }}>{t('admin.documents.totalRow')}</td>
                          <td style={{ padding: '13px 14px', color: theme.ink }}>{revenue.totals.rentals ?? '—'}</td>
                          <td style={{ padding: '13px 14px', whiteSpace: 'nowrap' }}>{money(revenue.totals.rental_revenue)}</td>
                          <td style={{ padding: '13px 14px', color: theme.amber, whiteSpace: 'nowrap' }}>{money(revenue.totals.late_fees)}</td>
                          <td style={{ padding: '13px 14px', whiteSpace: 'nowrap' }}>{money(revenue.totals.deposits_held)}</td>
                          <td style={{ padding: '13px 14px', whiteSpace: 'nowrap' }}>{money(revenue.totals.deposits_refunded)}</td>
                          <td style={{ padding: '13px 14px', whiteSpace: 'nowrap' }}>{money(revenue.totals.net)}</td>
                          <td style={{ padding: '13px 14px', whiteSpace: 'nowrap' }}>{money(revenue.totals.vat)}</td>
                          <td style={{ padding: '13px 14px', whiteSpace: 'nowrap', color: theme.greenDeep }}>{money(revenue.totals.gross)}</td>
                        </tr>
                      </tfoot>
                    )}
                  </table>
                </div>
              </div>
            )}

            <p style={{ color: theme.grey400, fontSize: 12, lineHeight: 1.7, marginTop: 12 }}>
              <FileText size={12} style={{ verticalAlign: 'middle', marginRight: 6 }} />
              {t('admin.documents.revenueNote')}
            </p>
          </div>
        )}
      </div>

      {/* Shown when no window could be opened for the document — the app's
          WebView refuses window.open and cannot save a file either, so this is
          the only place the invoice can actually be read there. */}
      {docView && (
        <div style={{ position: 'fixed', inset: 0, background: theme.alpha.black(0.75), zIndex: 1100, display: 'flex' }}>
          <div style={{ background: theme.white, margin: 'auto', width: '100%', maxWidth: 820, height: '92vh', display: 'flex', flexDirection: 'column', borderRadius: 8, overflow: 'hidden' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12, padding: '14px 18px', borderBottom: `1px solid ${theme.sand}` }}>
              <span style={{ fontFamily: theme.font.mono, fontSize: 13, color: theme.grey600 }}>{docView.title}</span>
              <button onClick={() => setDocView(null)}
                style={{ background: 'none', border: `1px solid ${theme.sand}`, color: theme.grey600, padding: '8px 18px', borderRadius: 4, fontSize: 12, letterSpacing: '0.08em', cursor: 'pointer', fontFamily: theme.font.body }}>
                {t('admin.documents.close')}
              </button>
            </div>
            <iframe
              title={docView.title || 'document'}
              srcDoc={docView.html}
              style={{ flex: 1, border: 'none', width: '100%', background: theme.white }}
            />
          </div>
        </div>
      )}
    </div>
  );
}

const labelStyle = { display: 'block', fontSize: 11, color: theme.grey500, letterSpacing: '0.08em', marginBottom: 6 };
const inputStyle = { padding: '10px 12px', border: `1px solid ${theme.sand}`, borderRadius: 4, fontSize: 13, fontFamily: theme.font.body, boxSizing: 'border-box', background: theme.white };
